import React from 'react'

import { Field, Label, Switch } from '@headlessui/react'

// Available toggle sizes
type size = 'small' | 'default' | 'large'

interface ToggleProps {
  value?: boolean // Required toggle value
  label?: string // Optional label prop
  disabled?: boolean // Optional disabled prop
  size?: size // Optional size prop
  onValueChange?: (value: boolean) => void // Optional value change handler
}

// Define a mapping of size to corresponding classes for the toggle container
const containerSizeClassMap: Record<size, string> = {
  small: 'h-[22px] w-[44px]',
  default: 'h-[28px] w-[54px]',
  large: 'h-[32px] w-[64px]',
}

// Define a mapping of size to corresponding font sizes for the toggle inner view
const innerSpanSizeClassMap: Record<size, string> = {
  small: 'h-[18px] w-[18px]',
  default: 'h-[24px] w-[24px]',
  large: 'h-[28px] w-[28px]',
}

// Define a mapping of size to corresponding font sizes for the toggle inner view
const innerSpanTransitionClassMap: Record<size, string> = {
  small: 'translate-x-[22px]',
  default: 'translate-x-[26px]',
  large: 'translate-x-[32px]',
}

// Define a mapping of size to corresponding font sizes
const fontSizeMap: Record<size, string> = {
  small: 'text-[0.75rem]',
  default: 'text-[0.8rem]',
  large: 'text-[1rem]',
}

const Toggle: React.FC<ToggleProps> = ({
  value, // current toggle value
  label, // default is empty
  disabled, // default is false
  size = 'default', // default is 'default'
  onValueChange,
}) => {
  return (
    <Field>
      <div className="flex flex-row flex-wrap items-center">
        <Switch
          disabled={disabled}
          checked={value}
          onChange={onValueChange}
          className={`${value ? 'bg-green-700' : 'bg-gray-300'} ${
            containerSizeClassMap[size]
          } relative inline-flex shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring focus:ring-green-400 disabled:opacity-60`}
        >
          {value && (
            <span
              className={`${fontSizeMap[size]} absolute ml-1 self-center text-white`}
            >
              on
            </span>
          )}
          <span
            aria-hidden="true"
            className={`${
              value ? innerSpanTransitionClassMap[size] : 'translate-x-0'
            } pointer-events-none inline-block ${
              innerSpanSizeClassMap[size]
            } transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
          />
        </Switch>
        <Label className={`${fontSizeMap[size]} ml-2`}>{label}</Label>
      </div>
    </Field>
  )
}

export default Toggle
